import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  ThumbsUp,
  KeyRound,
  RefreshCw,
  Unplug,
  CheckCircle2,
  AlertCircle,
  Upload,
  ExternalLink,
  Trash2,
  Download,
  Sparkles,
  Film,
  PlayCircle,
  Copy,
  Link2,
  Users,
  Eye,
  MessageCircle,
  Share2,
  Timer
} from "lucide-react";
import { Button } from "./ui/button";

// Pestaña «Facebook» (solo admin): conexión de la Página de Facebook y sus Reels. Solo /api/admin/fb/*.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/admin/fb`;

const authHeaders = () => {
  const token = localStorage.getItem("cortoviral_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};
const opts = () => ({ headers: authHeaders(), withCredentials: true });

const fmt = (n) => {
  if (n === null || n === undefined) return "—";
  if (n >= 1e6) return `${(n / 1e6).toFixed(1).replace(".", ",")} M`;
  if (n >= 1e4) return `${Math.round(n / 1e3)} K`;
  return String(n);
};

const STATUS = {
  writing: { label: "Escribiendo guion", style: "bg-blue-500/15 text-blue-300 border-blue-500/30", busy: true },
  queued: { label: "En cola de render", style: "bg-slate-700/40 text-slate-300 border-white/10", busy: true },
  rendering: { label: "Montando", style: "bg-blue-500/15 text-blue-300 border-blue-500/30", busy: true },
  uploading: { label: "Subiendo", style: "bg-blue-500/15 text-blue-300 border-blue-500/30", busy: true },
  ready: { label: "Listo, sin subir", style: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  published: { label: "Subido", style: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  failed: { label: "Fallido", style: "bg-red-500/15 text-red-300 border-red-500/30" }
};
const FB_STATE = { processing: "Procesando en Facebook", published: "Publicado", draft: "Borrador", error: "Rechazado por Facebook", unknown: "Sin confirmar" };

const CATEGORIES = {
  curiosidades: "Curiosidades", ciencia: "Ciencia", espacio: "Espacio", historia: "Historia", misterios: "Misterios",
  psicologia: "Psicología", dinero: "Dinero", productividad: "Productividad", salud: "Salud",
  tecnologia: "Tecnología e IA", animales: "Animales", mundo: "Países y lugares", records: "Récords", comida: "Comida"
};

export const FacebookView = () => {
  const [status, setStatus] = useState(null);
  const [videos, setVideos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [appCreds, setAppCreds] = useState({ app_id: "", app_secret: "" });
  const [manualToken, setManualToken] = useState("");
  const [showToken, setShowToken] = useState(false);
  const [create, setCreate] = useState({ topic: "", category: "curiosidades", upload: true });
  const [working, setWorking] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [preview, setPreview] = useState(null);
  const [copied, setCopied] = useState(false);
  const timer = useRef(null);

  const loadStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/account`, { params: { origin: window.location.origin }, ...opts() });
      setStatus(res.data);
    } catch (e) {
      setFeedback({ type: "error", text: e.response?.data?.detail || "No se pudo cargar la conexión con Facebook." });
    }
  }, []);

  const loadVideos = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/videos`, opts());
      setVideos(res.data.videos);
      setBusy(res.data.busy);
    } catch (e) {
      /* se reintenta en el siguiente refresco */
    }
  }, []);

  useEffect(() => {
    loadStatus();
    loadVideos();
    const onMsg = (ev) => {
      if (ev.data?.type === "FB_CONNECTED") {
        setWorking(null);
        setFeedback({ type: "success", text: ev.data.message || "Facebook conectado." });
        loadStatus();
      } else if (ev.data?.type === "FB_ERROR") {
        setWorking(null);
        setFeedback({ type: "error", text: ev.data.message || "Facebook rechazó la conexión." });
        loadStatus();
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [loadStatus, loadVideos]);

  const anyBusy = busy || videos.some((v) => STATUS[v.status]?.busy ||
    (v.fb_state === "processing" && Date.now() - new Date(v.published_at).getTime() < 3600 * 1000));
  useEffect(() => {
    clearInterval(timer.current);
    if (anyBusy) timer.current = setInterval(() => { loadVideos(); loadStatus(); }, 5000);
    return () => clearInterval(timer.current);
  }, [anyBusy, loadVideos, loadStatus]);

  const run = async (key, fn, okText) => {
    setWorking(key);
    setFeedback(null);
    try {
      const msg = await fn();
      if (okText || msg) setFeedback({ type: "success", text: msg || okText });
    } catch (e) {
      setFeedback({ type: "error", text: e.response?.data?.detail || "Ha habido un error." });
    } finally {
      setWorking((w) => (w === key ? null : w));
    }
  };

  const saveApp = () => run("app", async () => {
    const res = await axios.post(`${API}/account/app`, appCreds, opts());
    setStatus(res.data);
    setAppCreds({ app_id: "", app_secret: "" });
  }, "App de Meta guardada. Ahora pulsa «Conectar con Facebook».");

  const connect = async () => {
    setFeedback(null);
    setWorking("connect");
    try {
      const res = await axios.get(`${API}/account/auth-url`, { params: { origin: window.location.origin }, ...opts() });
      const popup = window.open(res.data.auth_url, "cortoviral_fb_oauth", "width=650,height=780,left=200,top=80,resizable=yes,scrollbars=yes");
      if (!popup) {
        window.location.href = res.data.auth_url;
        return;
      }
      setFeedback({ type: "info", text: "En la ventana de Facebook marca la Página donde quieres publicar y acepta los permisos. Al terminar, esta pantalla se actualiza sola." });
      const t = setInterval(() => {
        if (popup.closed) {
          clearInterval(t);
          setWorking(null);
          loadStatus();
        }
      }, 800);
    } catch (e) {
      setWorking(null);
      setFeedback({ type: "error", text: e.response?.data?.detail || "Error iniciando la conexión con Facebook." });
    }
  };

  const sendToken = () => run("token", async () => {
    const res = await axios.post(`${API}/account/token`, { token: manualToken }, opts());
    setStatus(res.data);
    setManualToken("");
    setShowToken(false);
    return res.data.message;
  });

  const choosePage = (pageId) => run("page", async () => {
    const res = await axios.post(`${API}/account/page`, { page_id: pageId }, opts());
    setStatus(res.data);
    return `Publicará en: ${res.data.page_name}.`;
  });

  const disconnect = () => {
    if (!window.confirm("¿Desconectar la Página de Facebook? La app de Meta guardada se conserva para volver a conectar.")) return;
    run("disconnect", async () => {
      const res = await axios.post(`${API}/account/disconnect`, {}, opts());
      setStatus(res.data);
    }, "Página desconectada.");
  };

  const forgetApp = () => {
    if (!window.confirm("¿Borrar también el App ID y el App Secret guardados?")) return;
    run("forget", async () => {
      const res = await axios.post(`${API}/account/forget-app`, {}, opts());
      setStatus(res.data);
    }, "App de Meta borrada.");
  };

  const createVideo = () => run("create", async () => {
    await axios.post(`${API}/videos/create`, create, opts());
    setCreate((c) => ({ ...c, topic: "" }));
    setBusy(true);
    loadVideos();
  }, "Creando el Reel. Aparecerá abajo en un par de minutos.");

  const uploadVideo = (v) => run(`up-${v.id}`, async () => {
    await axios.post(`${API}/videos/${v.id}/upload`, {}, opts());
    loadVideos();
    loadStatus();
  }, "Reel subido a Facebook. Meta tarda unos minutos en procesarlo.");

  const deleteVideo = (v) => {
    if (!window.confirm(`¿Borrar «${v.title}» de CortoViral? (Si ya está en Facebook, allí se queda.)`)) return;
    run(`del-${v.id}`, async () => {
      await axios.delete(`${API}/videos/${v.id}`, opts());
      if (preview === v.id) setPreview(null);
      loadVideos();
    });
  };

  const copyUri = () => {
    navigator.clipboard.writeText(status.redirect_uri);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!status) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-slate-400 text-sm gap-2">
        <span className="flex items-center gap-2"><RefreshCw className="w-4 h-4 animate-spin" /> Cargando Facebook...</span>
        {feedback && <span className="text-red-400 text-xs">{feedback.text}</span>}
      </div>
    );
  }

  const fbStyle = feedback?.type === "success"
    ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-300"
    : feedback?.type === "info" ? "bg-blue-500/10 border border-blue-500/30 text-blue-200"
      : "bg-red-500/10 border border-red-500/30 text-red-300";
  const choosing = !status.connected && status.pages.length > 1;

  const tokenBox = (
    <div className="space-y-2">
      <button onClick={() => setShowToken((x) => !x)} className="text-[11px] text-slate-400 hover:text-white underline">
        {showToken ? "Ocultar" : "¿Prefieres pegar un token del Explorador de la Graph API?"}
      </button>
      {showToken && (
        <div className="space-y-2">
          <input type="password" placeholder="Token de acceso de usuario o de Página" value={manualToken} autoComplete="new-password"
            onChange={(e) => setManualToken(e.target.value)}
            className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500/60" />
          <Button onClick={sendToken} disabled={working !== null || manualToken.trim().length < 40}
            className="w-full bg-slate-100 hover:bg-white text-slate-950 font-bold text-xs flex items-center justify-center gap-1.5">
            {working === "token" ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />}
            Conectar con este token
          </Button>
          <p className="text-[10px] text-slate-500">
            Con la app de Meta guardada, un token de usuario se alarga solo y el de la Página deja de caducar. Sin ella, el token caduca en unas horas.
          </p>
        </div>
      )}
    </div>
  );

  return (
    <div data-testid="fb-view" className="space-y-6">
      <div className="bg-gradient-to-br from-[#12141D] to-[#0F1A30] border border-blue-500/30 rounded-2xl p-6 shadow-2xl space-y-4">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {status.connected && status.page_picture ? (
              <img src={status.page_picture} alt="" className="w-12 h-12 rounded-xl border border-blue-500/40 shrink-0" />
            ) : (
              <div className="w-12 h-12 rounded-xl bg-blue-500/15 border border-blue-500/40 text-blue-300 flex items-center justify-center shrink-0">
                <ThumbsUp className="w-6 h-6" />
              </div>
            )}
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-lg font-black text-white">Facebook</h3>
                <span className="text-[10px] uppercase font-mono font-bold px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-300 border border-blue-500/30">Solo admin</span>
                {status.connected ? (
                  <span className="text-[10px] uppercase font-mono font-bold px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/40">
                    Página: {status.page_name}
                  </span>
                ) : (
                  <span className="text-[10px] uppercase font-mono font-bold px-2 py-0.5 rounded-full bg-slate-800 text-slate-400">Sin conectar</span>
                )}
              </div>
              <p className="text-xs text-slate-400 mt-0.5 max-w-2xl">
                Reels verticales en tu Página de Facebook. Va aparte de YouTube: su propia conexión con Meta, sus propios vídeos
                (no salen en «Mis Shorts») y no gasta créditos ni cuota de YouTube.
              </p>
            </div>
          </div>
          {status.connected && (
            <div className="flex flex-wrap items-center gap-2">
              {status.followers !== null && status.followers !== undefined && (
                <span className="text-[11px] text-slate-300 font-mono flex items-center gap-1"><Users className="w-3 h-3" /> {fmt(status.followers)} seguidores</span>
              )}
              <span className="text-[11px] text-slate-400 font-mono">Reels en 24 h: {status.uploads_today}/{status.max_uploads}</span>
              {status.page_link && (
                <a href={status.page_link} target="_blank" rel="noopener noreferrer" className="text-[11px] text-blue-300 hover:text-blue-200 flex items-center gap-1">
                  <ExternalLink className="w-3 h-3" /> Ver Página
                </a>
              )}
              {status.pages.length > 1 && (
                <select value={status.page_id} onChange={(e) => choosePage(e.target.value)} disabled={working !== null}
                  className="bg-black/40 border border-white/10 rounded-lg px-2 py-1 text-xs text-white" title="Cambiar de Página">
                  {status.pages.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              )}
              <Button variant="ghost" size="sm" onClick={disconnect} disabled={working !== null}
                className="h-8 text-xs text-slate-400 hover:text-red-300 flex items-center gap-1">
                <Unplug className="w-3.5 h-3.5" /> Desconectar
              </Button>
            </div>
          )}
        </div>

        {feedback && (
          <div className={`p-3 rounded-xl text-xs flex items-start gap-2 break-words ${fbStyle}`}>
            {feedback.type === "success" ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" /> : <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />}
            <span className="min-w-0">{feedback.text}</span>
          </div>
        )}
        {status.last_error && (
          <p className="text-[11px] text-red-400 break-words">Último error: {status.last_error}</p>
        )}

        {choosing && (
          <div className="p-4 rounded-xl bg-black/40 border border-blue-500/30 space-y-2">
            <p className="text-xs font-bold text-white">Tu cuenta administra varias Páginas. ¿En cuál publico los Reels?</p>
            <div className="flex flex-wrap gap-2">
              {status.pages.map((p) => (
                <Button key={p.id} onClick={() => choosePage(p.id)} disabled={working !== null}
                  className="h-9 bg-slate-900 hover:bg-slate-800 border border-white/10 text-white text-xs flex items-center gap-2">
                  {p.picture && <img src={p.picture} alt="" className="w-5 h-5 rounded" />} {p.name}
                </Button>
              ))}
            </div>
          </div>
        )}

        {!status.connected && !choosing && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="p-4 rounded-xl bg-black/40 border border-white/5 text-xs text-slate-300 space-y-2">
              <p className="font-bold text-white flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5 text-blue-300" /> App de Meta para tu Página</p>
              <ol className="list-decimal list-inside space-y-1 text-slate-400">
                <li>En <strong className="text-slate-200">developers.facebook.com</strong> crea una app (caso de uso: gestionar todo en tu Página).</li>
                <li>Añade los permisos: {status.scopes.join(", ")}.</li>
                <li>En «Inicio de sesión con Facebook» → Configuración, pon esta URI de redirección OAuth válida:</li>
              </ol>
              <div className="flex items-center gap-2">
                <code className="flex-1 min-w-0 truncate bg-black/50 border border-white/10 rounded px-2 py-1 text-[11px] text-blue-200">{status.redirect_uri}</code>
                <Button size="sm" variant="ghost" onClick={copyUri} className="h-7 px-2 text-[11px] text-slate-300 hover:text-white flex items-center gap-1">
                  <Copy className="w-3 h-3" /> {copied ? "Copiada" : "Copiar"}
                </Button>
              </div>
              <p className="text-slate-400">4. Pega aquí el App ID y el App Secret (Configuración → Básica) y pulsa «Conectar con Facebook».</p>
              <p className="text-slate-500">Como solo publicas en tu propia Página y eres administrador de la app, en principio no hace falta pasar la revisión de Meta.</p>
            </div>
            <div className="space-y-3">
              {status.has_app ? (
                <div className="p-3 rounded-xl bg-black/40 border border-white/5 text-xs text-slate-300 space-y-2">
                  <p>App de Meta guardada: <code className="text-blue-200">{status.app_id}</code></p>
                  <div className="flex flex-wrap gap-2">
                    <Button data-testid="fb-connect-btn" onClick={connect} disabled={working !== null}
                      className="bg-blue-600 hover:bg-blue-500 text-white font-black text-xs flex items-center gap-1.5">
                      {working === "connect" ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />}
                      Conectar con Facebook
                    </Button>
                    <Button variant="ghost" size="sm" onClick={forgetApp} disabled={working !== null}
                      className="h-9 text-xs text-slate-400 hover:text-red-300">Cambiar app</Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <input type="text" inputMode="numeric" placeholder="App ID (solo números)" value={appCreds.app_id} autoComplete="off"
                    onChange={(e) => setAppCreds((c) => ({ ...c, app_id: e.target.value }))}
                    className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500/60" />
                  <input type="password" placeholder="App Secret" value={appCreds.app_secret} autoComplete="new-password"
                    onChange={(e) => setAppCreds((c) => ({ ...c, app_secret: e.target.value }))}
                    className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500/60" />
                  <Button data-testid="fb-save-app-btn" onClick={saveApp} disabled={working !== null || !appCreds.app_id || !appCreds.app_secret}
                    className="w-full bg-blue-600 hover:bg-blue-500 text-white font-black text-xs flex items-center justify-center gap-1.5">
                    {working === "app" ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />}
                    Guardar app de Meta
                  </Button>
                  <p className="text-[10px] text-slate-500">Se guardan solo en el servidor y nunca se devuelven al navegador.</p>
                </div>
              )}
              {tokenBox}
            </div>
          </div>
        )}
      </div>

      <div className="bg-[#12141D] border border-white/10 rounded-2xl p-6 space-y-3">
        <h4 className="text-sm font-black text-white flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-blue-300" /> Crear un Reel sobre un tema</h4>
        <div className="flex flex-col lg:flex-row gap-2">
          <input type="text" maxLength={200} placeholder="Por ejemplo: por qué los gatos duermen 16 horas al día"
            value={create.topic} onChange={(e) => setCreate((c) => ({ ...c, topic: e.target.value }))}
            className="flex-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500/60" />
          <select value={create.category} onChange={(e) => setCreate((c) => ({ ...c, category: e.target.value }))}
            className="bg-black/40 border border-white/10 rounded-lg px-2 py-2 text-sm text-white">
            {Object.entries(CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <label className="flex items-center gap-1.5 text-xs text-slate-300 px-1">
            <input type="checkbox" checked={create.upload} onChange={(e) => setCreate((c) => ({ ...c, upload: e.target.checked }))} className="accent-blue-500 w-4 h-4" />
            Subir al terminar
          </label>
          <Button data-testid="fb-create-btn" onClick={createVideo} disabled={working !== null || busy || create.topic.trim().length < 5}
            title={busy ? "Ya se está creando un Reel; espera a que termine" : ""}
            className="bg-blue-600 hover:bg-blue-500 text-white font-black text-xs flex items-center gap-1.5">
            {working === "create" ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Film className="w-3.5 h-3.5" />}
            Crear Reel
          </Button>
        </div>
        <p className="text-[10px] text-slate-500">Se hace uno a la vez. Si se está montando un Short de YouTube, el Reel espera su turno.</p>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-black text-white">Reels de Facebook ({videos.length})</h4>
          <Button variant="ghost" size="sm" onClick={loadVideos} className="h-7 text-xs text-slate-400 hover:text-white flex items-center gap-1">
            <RefreshCw className={`w-3.5 h-3.5 ${anyBusy ? "animate-spin" : ""}`} /> Actualizar
          </Button>
        </div>
        {videos.length === 0 ? (
          <div className="text-center py-12 text-sm text-slate-500 bg-[#12141D] border border-white/5 rounded-2xl">
            Todavía no hay Reels. Crea uno arriba o desde el Radar Facebook.
          </div>
        ) : (
          <div className="space-y-2">
            {videos.map((v) => {
              const st = STATUS[v.status] || STATUS.failed;
              const fileUrl = `${API}/videos/${v.id}/file`;
              const hasFile = v.video_url && !v.video_deleted;
              const s = v.stats;
              return (
                <div key={v.id} className="bg-[#12141D] border border-white/10 rounded-xl p-3 space-y-2 min-w-0">
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2">
                    <div className="min-w-0 space-y-0.5">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded border ${st.style}`}>
                          {st.busy && <RefreshCw className="w-2.5 h-2.5 inline mr-1 animate-spin" />}{st.label}
                        </span>
                        {v.status === "published" && v.fb_state && (
                          <span className={`text-[10px] uppercase font-bold ${v.fb_state === "error" ? "text-red-400" : "text-blue-300"}`}>
                            {FB_STATE[v.fb_state] || v.fb_state}
                          </span>
                        )}
                        <span className="text-[10px] uppercase font-bold text-blue-300">{CATEGORIES[v.category] || v.category}</span>
                        <span className="text-[10px] text-slate-500">
                          {v.source === "radar_viral" ? "Del Radar Viral" : v.source === "ideas" ? "Idea de la IA" : "Manual"} · {new Date(v.created_at).toLocaleString("es-ES")}
                        </span>
                      </div>
                      <p className="text-sm font-bold text-white break-words">{v.title}</p>
                      {v.progress && <p className={`text-[11px] break-words ${v.status === "failed" || v.fb_state === "error" ? "text-red-400" : "text-slate-400"}`}>{v.progress}</p>}
                      {s && (
                        <div className="text-[11px] font-mono text-slate-300 flex flex-wrap gap-x-3 pt-0.5">
                          <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {fmt(s.plays)}</span>
                          <span className="flex items-center gap-1"><ThumbsUp className="w-3 h-3" /> {fmt(s.reactions)}</span>
                          <span className="flex items-center gap-1"><MessageCircle className="w-3 h-3" /> {fmt(s.comments)}</span>
                          <span className="flex items-center gap-1"><Share2 className="w-3 h-3" /> {fmt(s.shares)}</span>
                          {s.avg_watch_s ? <span className="flex items-center gap-1"><Timer className="w-3 h-3" /> {String(s.avg_watch_s).replace(".", ",")} s</span> : null}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1.5 shrink-0">
                      {hasFile && (
                        <Button size="sm" variant="ghost" onClick={() => setPreview(preview === v.id ? null : v.id)}
                          className="h-7 px-2 text-[11px] text-slate-300 hover:text-white flex items-center gap-1">
                          <PlayCircle className="w-3 h-3" /> {preview === v.id ? "Ocultar" : "Ver"}
                        </Button>
                      )}
                      {hasFile && (
                        <a href={fileUrl} className="h-7 px-2 text-[11px] text-slate-300 hover:text-white flex items-center gap-1 rounded-md hover:bg-white/5">
                          <Download className="w-3 h-3" /> Descargar
                        </a>
                      )}
                      {v.status === "ready" && hasFile && (
                        <Button size="sm" onClick={() => uploadVideo(v)} disabled={working !== null || !status.connected}
                          title={status.connected ? "" : "Conecta una Página de Facebook"}
                          className="h-7 px-2.5 text-[11px] font-bold bg-blue-600 hover:bg-blue-500 text-white flex items-center gap-1">
                          {working === `up-${v.id}` ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                          Subir a Facebook
                        </Button>
                      )}
                      {v.fb_url && (
                        <a href={v.fb_url} target="_blank" rel="noopener noreferrer"
                          className="h-7 px-2.5 text-[11px] font-bold text-emerald-300 hover:text-emerald-200 flex items-center gap-1 rounded-md bg-emerald-500/10 border border-emerald-500/30">
                          <ExternalLink className="w-3 h-3" /> Ver en Facebook
                        </a>
                      )}
                      {!st.busy && (
                        <Button size="sm" variant="ghost" onClick={() => deleteVideo(v)} disabled={working !== null}
                          className="h-7 px-2 text-[11px] text-slate-500 hover:text-red-300 flex items-center gap-1">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                  {preview === v.id && hasFile && (
                    <video src={fileUrl} controls className="w-full max-w-[280px] aspect-[9/16] rounded-lg bg-black" />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
